import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useField } from 'formik'
import classNames from 'classnames'
import TextField from '@material-ui/core/TextField'
import { makeStyles } from '@material-ui/core/styles'
import errorIcon from 'images/overdue.svg'

const useStyles = (vstTheme) =>
  makeStyles(() => ({
    root: {},
    labelRoot: {
      position: 'relative',
      transform: 'none !important',
      color: '#1C1C1C',
      lineHeight: '20px',
      fontWeight: 500,
      margin: '0 0 7px',
    },
    focusedLabel: {
      color: '#1C1C1C !important',
    },
    labelError: {
      color: '#1C1C1C !important',
    },
    notchedOutline: {
      border: 'none',
    },
    inputRoot: {
      borderRadius: '4px',
      border: '1px solid rgba(0, 0, 0, 0.43)',
      '& > div': {
        color: '#1C1C1C',
        fontFamily: 'Roboto',
        fontSize: '16px',
        lineHeight: '24px',
        fontWeight: 400,
        paddingRight: '60px !important',
      },
    },
    editHighlight: {
      boxShadow: 'inset black 0px 0px 0px 2px',
    },
    inputError: {
      borderColor: `${vstTheme?.vstui?.color?.error} !important`,
      '& > svg': {
        fill: `${vstTheme?.vstui?.color?.error} !important`,
      },
    },
    helperTextRoot: {
      position: 'relative',
      fontFamily: 'Roboto',
      fontSize: '16px',
      lineHeight: '24px',
      marginLeft: '24px',
    },
    helperTextError: {
      color: '#D21C35 !important',
      '&::before': {
        content: "''",
        backgroundImage: `url(${errorIcon})`,
        transform: 'translate(-20px, 3px)',
        height: '16px',
        width: '16px',
        position: 'absolute',
        top: '0',
      },
    },
  }))()

const TextEdit = ({ theme, required, onChange, editedField, disabled, ...props }) => {
  const [field, meta] = useField(props)
  const classes = useStyles(theme)

  const onTextEdit = useCallback(
    (value) => {
      field.onChange(value) // Formik logic to update selection
      onChange(value) // Callback for parent component
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, field],
  )

  return (
    <TextField
      id={props.name}
      label={props.label}
      value={field.value}
      onBlur={field.onBlur}
      onChange={onTextEdit}
      error={meta.touched && meta.error ? true : false}
      helperText={meta.touched && meta.error ? meta.error : null}
      inputProps={{ name: props.name }}
      classes={{ root: classes.root }}
      required={required}
      disabled={disabled}
      type="text"
      InputProps={{
        classes: {
          notchedOutline: classes.notchedOutline,
          root: classNames(classes.inputRoot, { [classes.editHighlight]: editedField }),
          error: classes.inputError,
        },
      }}
      InputLabelProps={{
        classes: {
          root: classes.labelRoot,
          focused: classes.focusedLabel,
          error: classes.labelError,
        },
      }}
      FormHelperTextProps={{
        classes: {
          root: classes.helperTextRoot,
          error: classes.helperTextError,
        },
      }}
      fullWidth
      variant="outlined"
      margin="dense"
    />
  )
}

export default TextEdit

TextEdit.propTypes = {
  defaultValue: PropTypes.string,
  editedField: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  theme: PropTypes.object,
  disabled: PropTypes.bool,
}

TextEdit.defaultProps = {
  defaultValue: '',
  editedField: false,
  label: '',
  name: '',
  onChange: () => {},
  required: false,
  theme: {},
  disabled: false,
}
